<template>
    <div class="PaginatedCardGrid">
        <div v-if="!searchResults" class="text-center py-8">
            <p>Loading...</p>
        </div>
        
        <template v-else>
            <CButton
                v-if="hasPrevPage"
                :aria-current="pageLinks ? 'false' : undefined"
                class="mb-8"
                :disabled="disabled"
                :to="pageLinks ? pageRoute(range.startPage - 1) : undefined"
                variant="primary"
                @click="loadPrev()"
            >
                Zobraziť predošlé …
            </CButton>

            <div v-for="(items, pageNumber, index) in pagedHitsComputed" :key="pageNumber">
            <CLineBreak v-if="index > 0" class="mb-8 mt-4" dark-color :title="Number(pageNumber) + 1" />

            <CMasonryGrid
                v-if="masonry"
                mode="masonry"
                :items="items"
                :column-width="columnWidth"
                :gap="24"
                :ssr-columns="twoCols ? 2 : 4"
            >
                <template #default="{ item }">
                    <slot :item="item" name="item" />
                </template>
            </CMasonryGrid>
            <CMasonryGrid v-else :columns="twoCols ? 2 : 4">
                <slot v-for="(item, itemIndex) in items" :key="item.id || itemIndex" :item="item" name="item" />
            </CMasonryGrid>
            </div>

            <div>
                <CLineBreak class="mb-4 mt-8" :class="{ 'hidden': false }" dark-color />

                <div class="grid grid-cols-3 items-center">
                    <div class="order-1 col-span-3 mb-4 text-center md:order-2 md:col-span-1 md:mb-0">
                        <CButton
                            v-if="hasNextPage"
                            :aria-current="pageLinks ? 'false' : undefined"
                            class="w-full md:w-auto"
                            :disabled="disabled"
                            :to="pageLinks ? pageRoute(range.endPage + 1) : undefined"
                            variant="primary"
                            @click="loadNext()"
                        >
                            Zobraziť viac ({{ numberOfItemsOnNextPage }})
                        </CButton>
                    </div>

                    <div class="order-2 col-span-1 md:order-1">
                        <CButton icon="arrow-top" variant="primary-link" @click="scrollTop()"> Hore </CButton>
                    </div>

                    <div class="order-3 col-span-2 text-right md:col-span-1">
                        <Paging :disabled="disabled" :pages="pages" :value="currentPage" @input="changePage($event)" />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import type { PagedSearchResults } from '#shared/types/search'
import Paging from '@/components/dashboard/Paging.vue'

const props = defineProps({
    lang: {
        type: String as PropType<'sk' | 'en'>,
        default: 'sk'
    },
    searchResults: {
        type: Object as PropType<PagedSearchResults | null>,
        default: null
    },
    fullWidth: {
        type: Boolean,
        default: true
    },
    twoCols: {
        type: Boolean,
        default: false
    },
    // Flow cards in a masonry layout so variable-height cards pack tightly with
    // no ragged empty slots on the last row (used by the photo-less TalentBase list).
    masonry: {
        type: Boolean,
        default: false
    },
    columnWidth: {
        type: Number,
        default: 300
    },
    disabled: {
        type: Boolean,
        default: false
    },
    // Renders "Zobraziť viac" / "predošlé" as real links to ?page=N (other query params kept)
    // so crawlers reach every page. The listing must load pages from the `page` query param;
    // these two buttons then no longer emit `page`.
    pageLinks: {
        type: Boolean,
        default: false
    }
})

const route = useRoute()

const emit = defineEmits(['page'])

const pagedHitsComputed = computed(() => {
    // Fallback to creating pagedHits structure if it doesn't exist
    if (!props.searchResults?.pagedHits) {
        const pagedHits: Record<number, any[]> = {}
        pagedHits[props.searchResults?.page || 0] = props.searchResults?.hits || []
        return pagedHits
    }
    return props.searchResults.pagedHits
})

const pages = computed((): number => {
    return props.searchResults?.nbPages || 0
})

const range = computed((): { startPage: number; endPage: number } => {
    const pages = Object.keys(pagedHitsComputed.value).map(Number)
    return { startPage: Math.min(...pages) + 1, endPage: Math.max(...pages) + 1 }
})

const currentPage = computed((): number => {
    return (props.searchResults?.page || 0) + 1
})

const numberOfItemsOnNextPage = computed((): number => {
    if (!props.searchResults) return 0
    const totalRemainingItems = (props.searchResults.nbHits || 0) - range.value.endPage * (props.searchResults.hitsPerPage || 0)
    return Math.min(totalRemainingItems, props.searchResults.hitsPerPage || 0)
})

const hasPrevPage = computed((): boolean => {
    return range.value.startPage > 1
})

const hasNextPage = computed((): boolean => {
    return range.value.endPage < (props.searchResults?.nbPages || 0)
})

function scrollTop() {
    if (import.meta.client) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }
}

function changePage(page: number) {
    emit('page', page)
}

// Page 1 is the clean URL, without ?page=1
function pageRoute(page: number): RouteLocationRaw {
    return { query: { ...route.query, page: page > 1 ? String(page) : undefined } }
}

function loadNext() {
    // Links navigate on their own
    if (hasNextPage.value && !props.pageLinks) {
        changePage(range.value.endPage + 1)
    }
}

function loadPrev() {
    if (hasPrevPage.value && !props.pageLinks) {
        changePage(range.value.startPage - 1)
    }
}
</script>
